import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { EnforcementCase } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function EnforcementDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isEnforcement = hasAnyRole(persona?.roles, M14_ROLES.ENFORCEMENT_OFFICER);
  const isSenior = hasAnyRole(persona?.roles, M14_ROLES.SENIOR_LO);
  const caseQ = useApiQuery<EnforcementCase>(`/api/v1/m14/enforcement/${id}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (caseQ.loading) return <Spinner label="Loading case…" />;
  if (caseQ.error || !caseQ.data) {
    return (
      <div>
        <Link to="/licensing/enforcement" className="text-sm font-semibold text-brand-600 hover:underline">← Back to enforcement</Link>
        <div className="mt-4"><ErrorNote message={caseQ.error ?? 'Case not found'} /></div>
      </div>
    );
  }
  const c = caseQ.data;

  const run = async (path: string, body?: unknown) => {
    setBusy(path);
    setError(null);
    try {
      await api.post(`/api/v1/m14/enforcement/${id}/${path}`, body);
      caseQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/licensing/enforcement" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to enforcement</Link>
      <PageHeader title="Enforcement case" subtitle={`Licence ${c.licenceId}`} actions={<StatusBadge status={c.state} />} />

      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="card mb-5 p-5">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Breach</h2>
        <p className="text-sm text-slate-700">{c.breachDescription}</p>
        {c.showCauseIssuedAt ? <p className="mt-2 text-xs text-slate-500">Show-cause issued {formatDateTime(c.showCauseIssuedAt)}, deadline {formatDateTime(c.showCauseDeadline)}.</p> : null}
        {c.responseText ? <p className="mt-2 rounded bg-slate-50 p-2 text-xs">Response: {c.responseText}</p> : null}
        {c.decisionReason ? <p className="mt-2 text-xs text-slate-600">Decision ({c.decision}): {c.decisionReason}</p> : null}
        {c.appealGrounds ? <p className="mt-2 rounded bg-amber-50 p-2 text-xs">Appeal grounds: {c.appealGrounds}</p> : null}
        {c.appealOutcome ? <p className="mt-2 text-xs text-slate-600">Appeal outcome: {c.appealOutcome} — {c.appealReason}</p> : null}
      </div>

      {isEnforcement && c.state === 'OPEN' ? <ShowCauseForm onIssue={(deadline) => run('show-cause', { deadline })} busy={busy === 'show-cause'} /> : null}

      {c.state === 'SHOW_CAUSE_ISSUED' ? <RespondForm onRespond={(text) => run('respond', { responseText: text })} busy={busy === 'respond'} /> : null}

      {(isEnforcement || isSenior) && c.state === 'SHOW_CAUSE_ISSUED' ? (
        <DecideForm onDecide={(decision, reason, suspendUntil) => run('decide', { decision, reason, suspendUntil })} busy={busy === 'decide'} />
      ) : null}

      {c.state === 'DECIDED' && c.decision !== 'DISMISS' ? (
        <AppealForm onAppeal={(grounds) => run('appeal', { grounds })} busy={busy === 'appeal'} />
      ) : null}

      {isSenior && c.state === 'APPEALED' ? (
        <AppealDecideForm onDecide={(outcome, reason) => run('appeal/decide', { outcome, reason })} busy={busy === 'appeal/decide'} />
      ) : null}
    </div>
  );
}

function ShowCauseForm({ onIssue, busy }: { onIssue: (deadline: string) => void; busy: boolean }) {
  const [deadline, setDeadline] = useState('');
  return (
    <div className="card mb-5 p-5">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Issue a show-cause notice</h2>
      <div className="flex gap-2">
        <input type="date" className="field-input" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        <button className="btn-primary shrink-0" onClick={() => onIssue(new Date(deadline).toISOString())} disabled={busy || !deadline}>{busy ? 'Issuing…' : 'Issue notice'}</button>
      </div>
    </div>
  );
}

function RespondForm({ onRespond, busy }: { onRespond: (text: string) => void; busy: boolean }) {
  const [text, setText] = useState('');
  return (
    <div className="card mb-5 p-5">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Holder's response</h2>
      <Field label="Your response"><textarea className="field-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <button className="btn-primary mt-2" onClick={() => onRespond(text)} disabled={busy || text.length < 1}>{busy ? 'Sending…' : 'Submit response'}</button>
    </div>
  );
}

function DecideForm({ onDecide, busy }: { onDecide: (decision: 'SUSPEND' | 'REVOKE' | 'DISMISS', reason: string, suspendUntil?: string) => void; busy: boolean }) {
  const [decision, setDecision] = useState<'SUSPEND' | 'REVOKE' | 'DISMISS'>('DISMISS');
  const [reason, setReason] = useState('');
  const [suspendUntil, setSuspendUntil] = useState('');
  return (
    <div className="card mb-5 p-5">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Decide</h2>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Decision" className="!mb-0">
          <select className="field-input" value={decision} onChange={(e) => setDecision(e.target.value as typeof decision)}>
            <option value="DISMISS">Dismiss</option><option value="SUSPEND">Suspend</option><option value="REVOKE">Revoke</option>
          </select>
        </Field>
        {decision === 'SUSPEND' ? <Field label="Suspend until" className="!mb-0"><input type="date" className="field-input" value={suspendUntil} onChange={(e) => setSuspendUntil(e.target.value)} /></Field> : null}
        <Field label="Reason" className="col-span-2 !mb-0"><input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </div>
      <button className="btn-danger mt-2" onClick={() => onDecide(decision, reason, suspendUntil ? new Date(suspendUntil).toISOString() : undefined)} disabled={busy || reason.length < 3}>{busy ? 'Deciding…' : 'Submit decision'}</button>
    </div>
  );
}

function AppealForm({ onAppeal, busy }: { onAppeal: (grounds: string) => void; busy: boolean }) {
  const [grounds, setGrounds] = useState('');
  return (
    <div className="card mb-5 p-5">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Appeal this decision</h2>
      <Field label="Grounds"><textarea className="field-input" rows={2} value={grounds} onChange={(e) => setGrounds(e.target.value)} /></Field>
      <button className="btn-secondary mt-2" onClick={() => onAppeal(grounds)} disabled={busy || grounds.length < 5}>{busy ? 'Appealing…' : 'Lodge appeal'}</button>
    </div>
  );
}

function AppealDecideForm({ onDecide, busy }: { onDecide: (outcome: 'UPHELD' | 'OVERTURNED', reason: string) => void; busy: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <div className="card mb-5 p-5">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Decide the appeal</h2>
      <Field label="Reason"><input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <div className="mt-2 flex gap-2">
        <button className="btn-success" onClick={() => onDecide('OVERTURNED', reason)} disabled={busy || reason.length < 3}>Overturn (reinstate licence)</button>
        <button className="btn-danger" onClick={() => onDecide('UPHELD', reason)} disabled={busy || reason.length < 3}>Uphold</button>
      </div>
    </div>
  );
}
